import { removeDupes, cleanObject } from '../utils/helpers';

const getGroupKey = payload => {
    if (payload.taskId) {
        return 'task';
    }

    if (payload.folderId) {
        return 'folder';
    }

    if (payload.projectId) {
        return 'project';
    }

    if (payload.userId) {
        if (payload.type) {
            return `user-${payload.type}`;
        }
        return 'user';
    }

    return null;
}

const getIDFromPayload = payload => payload.taskId || payload.folderId || payload.projectId || payload.userId;

const files = (state = {
    items: [],
    fetchInfo: {
        task: {},
        project: {},
        folder: {},
        user: {},
        'user-message': {},
        'user-task': {},
        'user-document': {}
    },
    processing: [],
    uploading: []
}, action) => {
    if (action.error) {
        return state;
    }

    switch (action.type) {
        case 'TEAM_CHANGE':
        case 'RESET_STORE':
            return {
                items: [],
                fetchInfo: {
                    task: {},
                    project: {},
                    folder: {},
                    user: {},
                    'user-message': {},
                    'user-task': {},
                    'user-document': {}
                },
                processing: [],
                uploading: []
            };

        case 'MARK_ALL_FOR_REFETCH':
            return Object.assign({}, state, {
                items: [...state.items.map(file => ({
                    ...file,
                    shouldRefetch: true
                }))]
            });

        case 'FILES_FETCH_REQUESTED': {
            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);

            if (!key) {
                return state;
            }

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: {
                        ...state.fetchInfo[key],
                        [id]: {
                            projectId: action.payload.projectId,
                            ...state.fetchInfo[key][id],
                            loading: true
                        }
                    }
                }
            });
        }

        case 'FILES_FETCH_SUCCEEDED': {
            if (!action.payload.files) {
                return state;
            }

            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);
            const items = removeDupes([...action.payload.files, ...state.items]);

            return Object.assign({}, state, {
                items,
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: {
                        ...state.fetchInfo[key],
                        [id]: {
                            ...(state.fetchInfo[key][id] || {}),
                            loading: false,
                            fetched: true,
                            error: false,
                            hasMore: action.payload.hasMore
                        }
                    }
                }
            });
        }

        case 'FILES_FETCH_FAILED': {
            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: {
                        ...state.fetchInfo[key],
                        [id]: {
                            ...(state.fetchInfo[key][id] || {}),
                            loading: false,
                            fetched: true,
                            error: true
                        }
                    }
                }
            });
        }
        case 'REFETCH_PROJECT_FILES': {
            const folders = { ...state.fetchInfo.folder };

            Object.keys(folders).forEach(folderId => {
                if (folders[folderId].projectId === action.payload.projectId) {
                    folders[folderId] = {
                        ...folders[folderId],
                        loading: false,
                        fetched: false,
                        error: false
                    }
                }
            });

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    folder: folders,
                    project: {
                        ...state.fetchInfo.project,
                        [action.payload.projectId]: {
                            fetched: false,
                            loading: false,
                            error: false
                        }
                    }
                }
            });
        }

        case 'FILES_UPLOAD_SUCCEEDED':
            if (!action.payload.files) {
                return state;
            }

            return Object.assign({}, state, {
                items: removeDupes([...action.payload.files.map(f => ({
                    ...f
                })), ...state.items]),
                uploading: [...state.uploading.map(file => {
                    if (file.id) {
                        if (action.payload.files.some(({ id }) => id === file.id)) {
                            return {
                                ...file,
                                progress: 1,
                                uploaded: new Date()
                            };
                        }
                    } else {
                        const payloadFile = action.payload.files.find(({ name }) => name === file.name);
                        if (payloadFile) {
                            return {
                                ...file,
                                id: payloadFile.id,
                                progress: 1,
                                uploaded: new Date()
                            };
                        }
                    }

                    return file;
                })]
            });

        case 'MESSAGE_SEND_SUCCEEDED':
            if (!action.payload.fragment.files || !action.payload.fragment.files.length) {
                return state;
            }

            return Object.assign({}, state, {
                uploading: [...state.uploading.map(file => {
                    if (file.id) {
                        if (action.payload.fragment.files.some(({ id }) => id === file.id)) {
                            return {
                                ...file,
                                progress: 1,
                                uploaded: new Date()
                            };
                        }
                    } else {
                        const payloadFile = action.payload.fragment.files.find(({ name }) => name === file.name);
                        if (payloadFile) {
                            return {
                                ...file,
                                id: payloadFile.id,
                                progress: 1,
                                uploaded: new Date()
                            };
                        }
                    }

                    return file;
                })]
            });

        case 'FILES_UPLOAD_PROGRESS':
        case 'MESSAGE_SEND_PROGRESS':
            if (!action.payload.files) {
                return state;
            }

            return Object.assign({}, state, {
                uploading: [...state.uploading.map(file => {
                    if (action.payload.files.some(f => f.name === file.name)) {
                        return {
                            ...file,
                            progress: action.payload.progress
                        };
                    }

                    return file;
                })],
                processing: state.processing.filter(file => action.payload.files.some(f => f.name === file.name))
            });

        case 'FILES_UPLOAD_FAILED':
            return Object.assign({}, state, {
                items: state.items.filter(file => file !== action.payload.fragment)
            });

        case 'FILE_ADD_SOCKET':
            if (state.items.find(file => file.id === action.payload.fileId)) {
                return Object.assign({}, state, {
                    items: [...state.items.map((file => {
                        if (file.id === action.payload.fileId) {
                            return Object.assign({}, file, {
                                project_id: action.payload.file.project_id,
                                created_at: action.payload.file.created_at,
                                download: action.payload.file.download,
                                url: action.payload.file.url,
                                thumbnail: action.payload.file.thumbnail
                            });
                        }

                        return file;
                    }))],
                    uploading: !action.payload.withUpload ? state.uploading : [...state.uploading.map(file => {
                        if (file.id) {
                            if (file.id === action.payload.fileId) {
                                return {
                                    ...file,
                                    progress: 1,
                                    uploaded: new Date()
                                };
                            }
                        } else {
                            if (file.name === action.payload.file.name) {
                                return {
                                    ...file,
                                    id: action.payload.fileId,
                                    progress: 1,
                                    uploaded: new Date()
                                };
                            }
                        }

                        return file;
                    })],
                    processing: !action.payload.withUpload ? state.processing :  state.processing.filter(file => file.name !== action.payload.file.name)
                });
            }

            return Object.assign({}, state, {
                items: [action.payload.file, ...state.items]
            });

        case 'FILE_FETCH_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items.filter(file => file.id !== action.payload.fileId), {
                    ...(state.items.find(file => file.id === action.payload.fileId) || {}),
                    id: action.payload.fileId,
                    loading: true
                }]
            });

        case 'FILE_FETCH_FAILED':
            return Object.assign({}, state, {
                items: [...state.items.filter(file => file.id !== action.payload.fileId), {
                    id: action.payload.fileId,
                    error: true
                }]
            });

        case 'FILE_FETCH_SUCCEEDED':
            if (!action.payload.file) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items.filter(file => file.id !== action.payload.file.id), {
                    ...action.payload.file,
                    project_id: action.payload.file.project_id || (action.payload.file.project && action.payload.file.project.id)
                }]
            });

        case 'FILE_UPDATE_REQUESTED':
        case 'FILE_UPDATE_SOCKET':
            if (!state.items.some(file => file?.id === action.payload.fileId)) {
                return Object.assign({}, state, {
                    items: [...state.items, action.payload.file]
                });
            }

            return Object.assign({}, state, {
                items: [...state.items].map(file => {
                    if (file?.id === action.payload.fileId) {
                        return Object.assign({}, file, cleanObject(action.payload.fileProps || action.payload.file));
                    }

                    return file;
                })
            });

        case 'FILE_LABELS_ADD_REQUESTED':
            if (!state.items.some(file => file.id === action.payload.fileId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(file => {
                    if (file.id === action.payload.fileId) {
                        return Object.assign({}, file, {
                            labels: [...new Set([...(file.labels || []), ...action.payload.labels])]
                        });
                    }

                    return file;
                })
            });

        case 'FILE_LABELS_REMOVE_REQUESTED':
            if (!state.items.some(file => file.id === action.payload.fileId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(file => {
                    if (file.id === action.payload.fileId) {
                        return Object.assign({}, file, {
                            labels: (file.labels || []).filter(labelId => !action.payload.labels.includes(labelId))
                        });
                    }

                    return file;
                })
            });

        case 'FILE_REMOVE_REQUESTED':
        case 'FILE_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(file => {
                    if (file.id === action.payload.fileId) {
                        return {
                            id: file.id,
                            removed: true
                        };
                    }

                    return file;
                })
            });

        case 'MESSAGE_ADD_SOCKET':
        case 'MESSAGE_REMOVE_SOCKET':
            if (action.payload.message && (action.payload.message.system || !state.items.some(file => file.id === action.payload.message.file))) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(file => {
                    if (action.payload.message && file.id === action.payload.message.file) {
                        return Object.assign({}, file, {
                            comments: action.type === 'MESSAGE_ADD_SOCKET' ? (file.comments || 0) + 1 : Math.max(0, (file.comments || 0) - 1)
                        });
                    }

                    return file;
                })
            });

        case 'FILE_PROCESS_START':
            return Object.assign({}, state, {
                processing: [...state.processing, action.payload.file]
            });

        case 'FILE_PROCESS_END':
            return Object.assign({}, state, {
                processing: state.processing.filter(file => !file.progress && file.name !== action.payload.file.name)
            });

        case 'FILE_UPLOAD_START':
            return Object.assign({}, state, {
                uploading: [...state.uploading, action.payload.file],
                processing: state.processing.filter(file => file.name !== action.payload.file.name)
            });

        case 'FILE_UPLOAD_ERROR':
            return Object.assign({}, state, {
                uploading: state.uploading.filter(file => file.name !== action.payload.file.name),
                processing: state.processing.filter(file => file.name !== action.payload.file.name)
            });

        case 'FILE_UPLOAD_PROGRESS':
            return Object.assign({}, state, {
                uploading: [...state.uploading.map(file => {
                    if (file.id) {
                        if (file.id === action.payload.fileId) {
                            return {
                                ...file,
                                progress: (action.payload.loaded / file.size) || 0
                            };
                        }
                    } else {
                        if (file.name === action.payload.fileName) {
                            return {
                                ...file,
                                id: action.payload.fileId,
                                progress: (action.payload.loaded / file.size) || 0
                            };
                        }
                    }

                    return file;
                })],
                processing: state.processing.filter(file => file.name !== action.payload.fileName)
            });

        case 'FILE_UPLOAD_REMOVE':
            return Object.assign({}, state, {
                uploading: state.uploading.filter(file => file.id !== action.payload.fileId)
            });

        case 'FILE_RELOAD_UPLOADING':
            return Object.assign({}, state, {
                uploading: [...state.uploading]
            });

        case 'TASK_REMOVE_REQUESTED':
        case 'TASK_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(file => {
                    if (file.task === action.payload.taskId) {
                        return {
                            id: file.id,
                            removed: true
                        };
                    }

                    return file;
                })
            });

        case 'PROJECT_REMOVE_REQUESTED':
        case 'PROJECT_REMOVE_SOCKET':
            if (!state.items.some(file => file.project === action.payload.projectId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: state.items.filter(file => !file.project || file.project !== action.payload.projectId)
            });

        default:
            return state;
    }
}

export default files;
